// Article-details reviewer/COI surface (WO B3 — SoftR parity, 07a §2). Reproduces the live
// article-details page top to bottom: preprint header, AI Suggested Reviewers (Claim, COI-12
// two-at-a-time rule), Incomplete AI Suggested Reviewers (Complete, evaluate-locked until COI
// resolves — COI-5), Student Proposed Reviewers with the LIVE two-axis COI status (COI-2/COI-10)
// + verdict evidence (COI-11) + advisory-shadow chip (COI-16), Rejected + Unable to Evaluate,
// "Manually Add Suggested Reviewer" (proposalSource=Manual), and the "Check COI" form (COI-9).
// Feature-flagged: only functional when /api/health reports features.coi.enabled (FEATURE_COI);
// otherwise redirects home. Live updates arrive via the app-wide useLiveUpdates SSE hook —
// "proposals"/"referees" query-key prefixes invalidate on reviewer.updated/coi.completed.
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  COI_REASON_LABELS,
  type PreprintView,
  type ProposalView,
  type RefereeView,
  type ReviewerStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Spinner,
  statusTone,
} from "../../components/ui.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { EvaluateModal } from "../reviewers/EvaluateModal.js";
import { CheckCoiForm } from "./CheckCoiForm.js";

// COI-12 rule text — surfaced VERBATIM (07a §2; the constant behind it is AI_CLAIM_LIMIT=2).
const CLAIM_RULE_TEXT =
  "you can only claim two AI suggested reviewers at a time; claiming a third returns the previous ones as 'Unable to Evaluate.'";

/** Proposal statuses that belong in the "Rejected + Unable to Evaluate" list (07a §2).
 *  Typed against ReviewerStatus so an unreachable member can't silently hide in the set —
 *  claim-limit releases arrive via `resolveStatus`, not `status` (see releasedUnable). */
const REJECTED_PROPOSAL_STATUSES = new Set<ReviewerStatus>(["Reject", "Student Reject", "COI Detected"]);
/** Referee (AI-candidate) statuses that belong in the same list. */
const REJECTED_REFEREE_STATUSES = new Set<string>(["Unable to Evaluate", "Reject"]);

/** COI-12 claim-limit release: the proposal's `status` stays "Unchecked" (and `incomplete`
 *  stays true), but `resolveStatus` is set — the row left the student's active plate. */
const releasedUnable = (p: ProposalView): boolean => p.resolveStatus === "Unable to Evaluate";

const doiHref = (doi: string): string => (doi.startsWith("http") ? doi : `https://doi.org/${doi}`);

/** Read a pitch-deck value as display text (linked-record arrays join with ", "). */
function pitchStr(pitch: Record<string, unknown>, key: string): string | null {
  const v = pitch[key];
  if (typeof v === "string" && v.trim()) return v;
  if (Array.isArray(v) && v.length > 0) return v.map(String).join(", ");
  return null;
}

// ── Route component: FEATURE_COI gate + preprint load ────────────────────────
export function ArticleReviewers(): JSX.Element {
  const { id = "" } = useParams();
  const { features, isLoading: featuresLoading, isError: featuresError, refetch: refetchFeatures } = useFeatures();
  const enabled = features.coi.enabled;

  const { data: preprint, isError } = useQuery({
    queryKey: qk.preprintOne(id),
    queryFn: () => api.get<PreprintView>(`/preprints/${id}`),
    enabled: !!id && enabled,
  });

  if (featuresLoading) return <Spinner label="Loading…" />;
  if (featuresError) {
    // The health fetch failed — we DON'T know whether the flag is on, so don't silently
    // redirect users off a surface the server may well have enabled. Offer a retry instead.
    return (
      <EmptyState>
        <p>Could not check whether the reviewer &amp; COI surface is enabled.</p>
        <Button variant="secondary" className="mt-3" onClick={refetchFeatures}>
          Retry
        </Button>
      </EmptyState>
    );
  }
  if (!enabled) return <Navigate to="/" replace />; // FEATURE_COI off ⇒ surface does not exist
  if (isError) {
    return (
      <EmptyState>
        Preprint not found.{" "}
        <Link className="text-brand hover:underline" to="/reviewers">
          Back to Find Reviewers
        </Link>
      </EmptyState>
    );
  }
  if (!preprint) return <Spinner label="Loading preprint…" />;
  return <ReviewersSurface preprint={preprint} />;
}

// ── The page proper ──────────────────────────────────────────────────────────
function ReviewersSurface({ preprint }: { preprint: PreprintView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const [evalProposal, setEvalProposal] = useState<ProposalView | null>(null);
  const [claimTarget, setClaimTarget] = useState<RefereeView | null>(null);
  const [manualOpen, setManualOpen] = useState(false);

  const ctx = {
    domain: preprint.domain,
    preprint: { miniTeamEmails: preprint.miniTeamEmails, assignedEditorEmail: preprint.assignedEditorEmail },
  };
  // FR-P8 / WO-7/21 Track H: ACTIVE is proposable in any domain; Dormant stays mini-team scoped.
  const isAe = can("advanceToPitchEditorial", {});
  const me = user?.email.toLowerCase();
  const isMiniMember = !!me && preprint.miniTeamEmails.some((e) => e.toLowerCase() === me);
  const statusProposable =
    isAe || preprint.status === "ACTIVE" || (preprint.status === "DORMANT" && isMiniMember);
  const proposeCtx = preprint.status === "ACTIVE" ? { preprint: ctx.preprint } : ctx;
  const canClaim = statusProposable && (isAe || can("claimEvaluateSuggestion", proposeCtx));
  const canSubmitCoi = statusProposable && (isAe || can("submitCoiCheck", proposeCtx));
  const canEvaluate = can("completeEvaluateForm", proposeCtx);
  const canStudentReject = can("studentRejectCandidate", proposeCtx);

  const { data: referees } = useQuery({
    queryKey: qk.referees(preprint.id),
    queryFn: () => api.get<RefereeView[]>(`/referees?preprintId=${preprint.id}`),
  });
  const { data: proposals } = useQuery({
    queryKey: qk.proposals({ preprintId: preprint.id }),
    queryFn: () => api.get<ProposalView[]>(`/proposals?preprintId=${preprint.id}`),
    refetchInterval: 10000, // belt-and-suspenders behind SSE (RT-4), matches ReviewersPortal
  });
  const { data: selected } = useQuery({
    queryKey: qk.selectedOne(preprint.selectedId ?? ""),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${preprint.selectedId}`),
    enabled: !!preprint.selectedId,
  });

  const studentReject = useMutation({
    mutationFn: (proposalId: string) => api.post(`/proposals/${proposalId}/student-reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals"] }),
  });

  const all = proposals ?? [];
  // 07a §2: the proposal lists are the STUDENT'S OWN ("your …"); the AI referee pool card
  // stays preprint-wide. Scope by proposer — case-insensitive, like the server predicates.
  const mine = (p: ProposalView): boolean =>
    !!user && (p.proposerEmail ?? "").toLowerCase() === user.email.toLowerCase();
  const pool = (referees ?? []).filter((r) => !REJECTED_REFEREE_STATUSES.has(r.status));
  const rejectedReferees = (referees ?? []).filter((r) => REJECTED_REFEREE_STATUSES.has(r.status));
  // Incomplete AI Suggested Reviewers: YOUR claim-born rows (refereeId set), Part 2 pending,
  // not rejected and not claim-limit-released (COI-12 sets resolveStatus, not status).
  const incompleteAi = all.filter(
    (p) =>
      mine(p) &&
      p.refereeId != null &&
      p.incomplete &&
      !REJECTED_PROPOSAL_STATUSES.has(p.status) &&
      !releasedUnable(p),
  );
  // Student Proposed Reviewers: YOUR Check COI submissions (Student) + Manual adds.
  const studentProposed = all.filter(
    (p) =>
      mine(p) &&
      (p.proposalSource === "Student" || p.proposalSource === "Manual") &&
      !REJECTED_PROPOSAL_STATUSES.has(p.status) &&
      !releasedUnable(p),
  );
  const rejectedProposals = all.filter(
    (p) => mine(p) && (REJECTED_PROPOSAL_STATUSES.has(p.status) || releasedUnable(p)),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Link className="text-brand hover:underline" to="/reviewers">
          ← Find Reviewers
        </Link>
      </div>

      {/* ── 1. Preprint header (07a §2) ─────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            {preprint.domain && <Badge tone="blue">{preprint.domain}</Badge>}
            <Badge tone={statusTone(preprint.status)}>{preprint.status}</Badge>
          </div>
          <h1 className="mt-1 text-xl font-bold">{preprint.title}</h1>
          <p className="text-xs text-slate-500">
            {preprint.doi ? (
              <a className="text-brand hover:underline" href={doiHref(preprint.doi)} target="_blank" rel="noreferrer">
                {preprint.doi}
              </a>
            ) : (
              "No DOI"
            )}
          </p>
        </CardHeader>
        <CardBody className="space-y-1 text-sm">
          {selected?.authors && (
            <p>
              <span className="text-xs font-medium text-slate-500">Authors: </span>
              {selected.authors}
            </p>
          )}
          {selected?.affiliations && (
            <p>
              <span className="text-xs font-medium text-slate-500">Affiliations: </span>
              {selected.affiliations}
            </p>
          )}
          {selected && pitchStr(selected.pitch, "Author_corr") && (
            <p>
              <span className="text-xs font-medium text-slate-500">Corresponding author: </span>
              {pitchStr(selected.pitch, "Author_corr")}
            </p>
          )}
          {selected?.assigneeEmail && (
            <p>
              <span className="text-xs font-medium text-slate-500">Graduate assignee: </span>
              {selected.assigneeEmail}
            </p>
          )}
          {selected && (pitchStr(selected.pitch, "Team") ?? pitchStr(selected.pitch, "Assigned Team")) && (
            <p>
              <span className="text-xs font-medium text-slate-500">Team: </span>
              {pitchStr(selected.pitch, "Team") ?? pitchStr(selected.pitch, "Assigned Team")}
            </p>
          )}
          {selected?.mostSimilarArticlesUrl && (
            <p>
              <a
                className="text-sm text-brand hover:underline"
                href={selected.mostSimilarArticlesUrl}
                target="_blank"
                rel="noreferrer"
              >
                Similar Articles ↗
              </a>
            </p>
          )}
        </CardBody>
      </Card>

      {/* ── 2. AI Suggested Reviewers (COI-12) ──────────────────────── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">AI Suggested Reviewers</h2>
          <p className="text-xs text-slate-500">{CLAIM_RULE_TEXT}</p>
        </CardHeader>
        <CardBody className="space-y-2">
          {pool.length === 0 ? (
            <EmptyState>No AI-suggested reviewers for this preprint.</EmptyState>
          ) : (
            pool.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="text-xs text-slate-500">
                    {r.affiliations ?? "—"} · H-index {r.hIndex ?? "?"}
                    {r.pubMedLink && (
                      <>
                        {" · "}
                        <a className="text-brand hover:underline" href={r.pubMedLink} target="_blank" rel="noreferrer">
                          PubMed ↗
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                  {r.status === "Unclaimed" && canClaim && (
                    <Button variant="secondary" className="py-1" onClick={() => setClaimTarget(r)}>
                      Claim
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>

      {/* ── 3. Incomplete AI Suggested Reviewers (COI-5 lock) ───────── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Incomplete AI Suggested Reviewers</h2>
          <p className="text-xs text-slate-500">Your claimed candidates whose Part-2 reviewer profile is still pending.</p>
        </CardHeader>
        <CardBody className="space-y-2">
          {incompleteAi.length === 0 ? (
            <p className="text-xs text-slate-400">Nothing awaiting completion.</p>
          ) : (
            incompleteAi.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {p.firstName} {p.lastName}
                  </div>
                  <div className="text-xs text-slate-500">{p.reviewerEmail || "no email"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <CoiStatusBadge p={p} />
                  {canEvaluate && (
                    <Button
                      className="py-1"
                      onClick={() => setEvalProposal(p)}
                      disabled={p.evaluateLocked}
                      title={p.evaluateLocked ? "Locked until COI resolves" : undefined}
                    >
                      {p.evaluateLocked ? "🔒 Complete" : "Complete"}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>

      {/* ── 4. Student Proposed Reviewers (live two-axis COI status) ── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Student Proposed Reviewers</h2>
          <p className="text-xs text-slate-500">
            Your submissions and their COI outcomes — status resolves live, no refresh needed.
          </p>
        </CardHeader>
        <CardBody className="space-y-2">
          {studentProposed.length === 0 ? (
            <EmptyState>No student-proposed reviewers yet — use the Check COI form below.</EmptyState>
          ) : (
            studentProposed.map((p) => (
              <ProposedRow
                key={p.id}
                p={p}
                canEvaluate={canEvaluate}
                canStudentReject={canStudentReject}
                onEvaluate={() => setEvalProposal(p)}
                onStudentReject={() => studentReject.mutate(p.id)}
              />
            ))
          )}
        </CardBody>
      </Card>

      {/* ── 5. Rejected + Unable to Evaluate ────────────────────────── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Rejected + Unable to Evaluate</h2>
          <p className="text-xs text-slate-500">
            Your rejected / released proposals, plus AI candidates no longer available on this preprint.
          </p>
        </CardHeader>
        <CardBody>
          {rejectedProposals.length === 0 && rejectedReferees.length === 0 ? (
            <p className="text-xs text-slate-400">Nothing here yet.</p>
          ) : (
            <ul className="space-y-1">
              {rejectedProposals.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="truncate">
                    {p.firstName} {p.lastName}
                    {p.reviewerEmail ? ` · ${p.reviewerEmail}` : ""}
                  </span>
                  {/* Claim-limit releases keep status "Unchecked" — show the resolveStatus. */}
                  <Badge tone={statusTone(p.resolveStatus ?? p.status)}>{p.resolveStatus ?? p.status}</Badge>
                </li>
              ))}
              {rejectedReferees.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="truncate">{r.name}</span>
                  <Badge tone="slate">{r.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* ── 6. Manually Add Suggested Reviewer (proposalSource=Manual) ─ */}
      {canSubmitCoi && (
        <div>
          <Button variant="secondary" onClick={() => setManualOpen(true)}>
            Manually Add Suggested Reviewer
          </Button>
        </div>
      )}

      {/* ── 7. The "Check COI" form (COI-9, 07a §1) ─────────────────── */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Check COI</h2>
          <p className="text-xs text-slate-500">
            Propose a reviewer — submitting creates a pending record and runs the COI check in the
            background; the result appears above without blocking you.
          </p>
        </CardHeader>
        <CardBody>
          {!statusProposable && !isAe && (
            <p className="mb-2 text-xs text-amber-700">
              You can propose reviewers once this preprint is Active
              {preprint.status === "DORMANT" ? " (or join its mini-team)" : ""}.
            </p>
          )}
          {canSubmitCoi ? (
            <CheckCoiForm preprintId={preprint.id} proposals={all} source="Student" />
          ) : (
            <p className="text-xs text-slate-500">You don&apos;t have access to propose reviewers for this preprint.</p>
          )}
        </CardBody>
      </Card>

      {claimTarget && (
        <ClaimModal
          referee={claimTarget}
          preprintId={preprint.id}
          onClose={() => setClaimTarget(null)}
          onClaimed={(created) => {
            setClaimTarget(null);
            // Mirror ReviewersPortal: the Part-2 form opens right after a claim — unless the
            // evaluate form is still COI-locked (COI-5), in which case it waits in list 3.
            if (!created.evaluateLocked) setEvalProposal(created);
          }}
        />
      )}
      {manualOpen && (
        <Modal open onClose={() => setManualOpen(false)} title="Manually Add Suggested Reviewer">
          <CheckCoiForm preprintId={preprint.id} proposals={all} source="Manual" />
        </Modal>
      )}
      {evalProposal && <EvaluateModal proposal={evalProposal} onClose={() => setEvalProposal(null)} />}
    </div>
  );
}

// ── Two-axis COI status badge (COI-2 / COI-10) ───────────────────────────────
function CoiStatusBadge({ p }: { p: ProposalView }): JSX.Element {
  if (p.coiPipelineState === "checking" || (p.coiPending && p.coiPipelineState !== "checked")) {
    return (
      <span className="animate-pulse">
        <Badge tone="amber">Checking…</Badge>
      </span>
    );
  }
  // Resolved = engine ran OR a verdict exists — legacy/walls rows can carry a verdict while
  // coiPipelineState is still "not_checked" (pre-two-axis-migration data).
  if (coiResolved(p)) {
    if (p.coiVerdict === "conflict") {
      const reasons =
        p.coiReasons.length > 0
          ? p.coiReasons.map((r) => COI_REASON_LABELS[r] ?? r).join("; ")
          : (p.coiSummary ?? "conflict detected");
      return <Badge tone="red">Conflict: {reasons}</Badge>;
    }
    if (p.coiVerdict === "unable_to_evaluate") return <Badge tone="slate">Unable to evaluate</Badge>;
    if (p.coiVerdict === "clear") return <Badge tone="green">Clear — evaluate now</Badge>;
    return <Badge tone="slate">Checked</Badge>;
  }
  return <Badge tone="slate">COI not run</Badge>;
}

/** A row counts as resolved when the engine has run OR a verdict exists (legacy rows). */
const coiResolved = (p: ProposalView): boolean => p.coiPipelineState === "checked" || p.coiVerdict != null;

/** Compact verdict-evidence line on resolved rows (COI-11). */
function EvidenceLine({ p }: { p: ProposalView }): JSX.Element | null {
  if (!coiResolved(p)) return null;
  const hasAny = p.commonInstitutions || p.prophyScore != null || p.prophyAuthorUrl || p.coiComment;
  if (!hasAny) return null;
  return (
    <p className="text-xs text-slate-500">
      {p.commonInstitutions && <>Common institutions: {p.commonInstitutions}</>}
      {p.commonInstitutions && (p.prophyScore != null || p.prophyAuthorUrl || p.coiComment) && " · "}
      {p.prophyScore != null && <>Prophy score {p.prophyScore}</>}
      {p.prophyScore != null && (p.prophyAuthorUrl || p.coiComment) && " · "}
      {p.prophyAuthorUrl && (
        <a className="text-brand hover:underline" href={p.prophyAuthorUrl} target="_blank" rel="noreferrer">
          Prophy profile ↗
        </a>
      )}
      {p.prophyAuthorUrl && p.coiComment && " · "}
      {p.coiComment && <>{p.coiComment}</>}
    </p>
  );
}

function ProposedRow({
  p,
  canEvaluate,
  canStudentReject,
  onEvaluate,
  onStudentReject,
}: {
  p: ProposalView;
  canEvaluate: boolean;
  canStudentReject: boolean;
  onEvaluate: () => void;
  onStudentReject: () => void;
}): JSX.Element {
  // COI-16: verdicts from the shadow Prophy backend are advisory — never authoritative writes.
  // Keyed solely on the per-row flag the engine persists; inferring from the CURRENT engine
  // would mislabel historical mock/walls verdicts after an engine flip.
  const advisory = p.coiAdvisory && p.coiVerdict != null;
  return (
    <div className="rounded-md border border-slate-200 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {p.firstName} {p.lastName}
          </div>
          <div className="text-xs text-slate-500">
            {p.reviewerEmail || "no email"} · {p.affiliation ?? "—"}
            {p.proposalSource === "Manual" && " · manually added"}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {p.incomplete && <Badge tone="amber">Part 2 pending</Badge>}
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <CoiStatusBadge p={p} />
        {advisory && <Badge tone="violet">advisory (shadow)</Badge>}
        <div className="ml-auto flex gap-1.5">
          {canEvaluate && (
            <Button
              className="py-1"
              onClick={onEvaluate}
              disabled={p.evaluateLocked}
              title={p.evaluateLocked ? "Locked until COI resolves" : undefined}
            >
              {p.evaluateLocked ? "🔒 Evaluate" : "Evaluate"}
            </Button>
          )}
          {canStudentReject && (
            <Button variant="ghost" className="py-1" onClick={onStudentReject} title="Student reject">
              ✕
            </Button>
          )}
        </div>
      </div>
      <EvidenceLine p={p} />
    </div>
  );
}

// ── Claim modal: reviewer email prompt + 409 wall errors inline ──────────────
function ClaimModal({
  referee,
  preprintId,
  onClose,
  onClaimed,
}: {
  referee: RefereeView;
  preprintId: string;
  onClose: () => void;
  onClaimed: (created: ProposalView) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [wallError, setWallError] = useState<{ message: string; requireBackup?: boolean } | null>(null);
  const claim = useMutation({
    mutationFn: () =>
      api.post<ProposalView>(`/referees/${referee.id}/claim`, {
        preprintId,
        ...(email.trim() ? { reviewerEmail: email.trim() } : {}),
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["referees"] });
      void qc.invalidateQueries({ queryKey: ["proposals"] });
      onClaimed(created);
    },
    onError: (err) => {
      // 409 wall errors — same shape ReviewersPortal handles: { error, wall, requireBackup? }.
      if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object") {
        const body = err.body as { error: string; requireBackup?: boolean };
        setWallError({ message: body.error, requireBackup: body.requireBackup });
      } else {
        setWallError({ message: "Could not claim this reviewer." });
      }
    },
  });
  return (
    <Modal open onClose={onClose} title={`Claim ${referee.name}`}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          claim.mutate();
        }}
      >
        <p className="text-xs text-slate-500">{CLAIM_RULE_TEXT}</p>
        <Field label="Reviewer email (optional — add it if you have it)">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reviewer@university.edu" />
        </Field>
        {wallError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
            <p>{wallError.message}</p>
            {wallError.requireBackup && (
              <p className="mt-1 font-medium">Propose them as a backup reviewer via the Check COI form instead.</p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={claim.isPending}>
            {claim.isPending ? "Claiming…" : "Claim"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
